<template>
  <div>
    <div v-if="!modelValue.length" class="text-muted small mb-2">Aucun médicament prescrit.</div>
    <div v-for="(l, i) in modelValue" :key="l.cle" class="ligne mb-2" :class="{ alerte: allergie(l) }">
      <div v-if="allergie(l)" class="alert py-1 px-2 small mb-2 d-flex flex-wrap align-items-center gap-2" :class="l.motifAllergie ? 'alert-warning' : 'alert-danger'">
        <span class="me-auto">
          <i class="bi bi-exclamation-octagon-fill me-1"></i> Le patient est allergique à « {{ allergie(l) }} ».
          <template v-if="l.motifAllergie"><br><strong>Prescription maintenue</strong><span v-if="l.allergieConfirmeePar"> par {{ l.allergieConfirmeePar }}</span> : {{ l.motifAllergie }}</template>
          <template v-else> Retirez ce médicament ou maintenez-le en indiquant le motif.</template>
        </span>
        <template v-if="modifiable(l)">
          <button type="button" class="btn btn-sm btn-outline-danger py-0" @click="retirer(i)"><i class="bi bi-trash me-1"></i>Retirer</button>
          <button type="button" class="btn btn-sm py-0" :class="l.motifAllergie ? 'btn-outline-secondary' : 'btn-danger'" @click="maintenir(l)">{{ l.motifAllergie ? 'Modifier le motif' : 'Maintenir…' }}</button>
        </template>
      </div>
      <div class="row g-2 align-items-start">
        <div class="col-md-5 position-relative">
          <template v-if="modifiable(l)">
            <input v-model="l.medicament" class="form-control" :placeholder="pharmacie ? 'Médicament (stock de la pharmacie ou saisie libre)' : 'Médicament, dosage, forme'"
                   @input="chercher(l)" @focus="chercher(l)" @blur="fermerPlusTard(l)">
            <div v-if="l.ouvert && l.propositions.length" class="list-group position-absolute w-100 shadow-sm propositions">
              <button v-for="p in l.propositions" :key="p.id" type="button" class="list-group-item list-group-item-action py-1 d-flex justify-content-between" @mousedown.prevent="choisir(l, p)">
                <span>{{ p.nom }} <small class="text-muted">{{ p.unite }}</small></span>
                <span class="badge" :class="p.stock > 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">{{ p.stock > 0 ? p.stock + ' en stock' : 'Rupture' }}</span>
              </button>
            </div>
            <small v-if="l.produitId" class="text-success"><i class="bi bi-box-seam me-1"></i>Produit de la pharmacie<span v-if="l.stock != null"> — {{ l.stock }} en stock</span></small>
          </template>
          <div v-else class="fw-semibold pt-1">{{ l.medicament }} <span v-if="l.delivre" class="badge bg-success ms-1">Délivré</span></div>
        </div>
        <div class="col-6 col-md-2">
          <input v-if="modifiable(l)" v-model.number="l.quantite" type="number" min="1" class="form-control" placeholder="Qté">
          <div v-else class="pt-1">× {{ l.quantite }}</div>
        </div>
        <div class="col-6 col-md-4">
          <select v-if="modifiable(l)" v-model="l.conditionnementId" class="form-select">
            <option :value="null">Forme / conditionnement…</option>
            <option v-for="c in conditionnements" :key="c.id" :value="c.id">{{ c.nom }}</option>
          </select>
          <div v-else class="small text-muted pt-1">{{ nom(conditionnements, l.conditionnementId) }}</div>
        </div>
        <div class="col-md-1 text-end">
          <button v-if="modifiable(l)" type="button" class="btn btn-sm btn-outline-danger" title="Retirer" @click="retirer(i)"><i class="bi bi-trash"></i></button>
        </div>
      </div>
      <div class="row g-2 mt-1">
        <div class="col-md-3">
          <select v-if="modifiable(l)" v-model="l.frequenceId" class="form-select form-select-sm">
            <option :value="null">Fréquence…</option>
            <option v-for="f in frequences" :key="f.id" :value="f.id">{{ f.nom }}</option>
          </select>
          <span v-else class="small">{{ nom(frequences, l.frequenceId) }}</span>
        </div>
        <div class="col-md-3">
          <select v-if="modifiable(l)" v-model="l.dureeId" class="form-select form-select-sm">
            <option :value="null">Durée…</option>
            <option v-for="d in durees" :key="d.id" :value="d.id">{{ d.nom }}</option>
          </select>
          <span v-else class="small">{{ nom(durees, l.dureeId) }}</span>
        </div>
        <div class="col-md-3">
          <input v-if="modifiable(l)" v-model="l.posologie" class="form-control form-control-sm" placeholder="Posologie (ex. 1 cp matin et soir)">
          <span v-else class="small">{{ l.posologie }}</span>
        </div>
        <div class="col-md-3">
          <input v-if="modifiable(l)" v-model="l.instructions" class="form-control form-control-sm" placeholder="Consignes (ex. après le repas)">
          <span v-else class="small fst-italic">{{ l.instructions }}</span>
        </div>
      </div>
    </div>
    <button v-if="!lectureSeule" type="button" class="btn btn-sm btn-outline-primary" @click="ajouter"><i class="bi bi-plus-circle me-1"></i>Ajouter un médicament</button>
    <div v-if="!lectureSeule && listesVides.length" class="small text-muted mt-2">
      <i class="bi bi-info-circle me-1"></i>Listes encore vides : {{ listesVides.map(l => l.libelle).join(', ') }}. Écrivez la posologie en clair, ou
      <template v-if="listesVides.some(l => peutOuvrir(l.to))">
        configurez-les :
        <template v-for="(l, i) in listesVides" :key="l.to"><router-link v-if="peutOuvrir(l.to)" :to="l.to">{{ l.libelle }}</router-link><span v-else>{{ l.libelle }}</span><span v-if="i < listesVides.length - 1">, </span></template>.
      </template>
      <template v-else>demandez à l'administrateur de les configurer (Configuration).</template>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { demanderMotif } from '@/components/commun/dialogue'

let compteur = 0

export function ligneOrdonnance(l = {}) {
  return {
    cle: ++compteur, id: l.id || null, produitId: l.produitId || null, medicament: l.medicament || '', quantite: l.quantite ?? 1,
    posologie: l.posologie || '', instructions: l.instructions || '', frequenceId: l.frequenceId || null, dureeId: l.dureeId || null,
    conditionnementId: l.conditionnementId || null, delivre: !!l.delivre,
    motifAllergie: l.motifAllergie || '', allergieConfirmeePar: l.allergieConfirmeePar || '', nomProduit: l.produitId ? l.medicament : null, stock: null, propositions: [], ouvert: false, minuteur: null,
  }
}

function sansAccents(t) {
  return String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/** Familles fréquentes : une allergie à la famille couvre ses molécules. */
const FAMILLES = {
  penicilline: ['amoxicilline', 'ampicilline', 'penicilline', 'oxacilline', 'cloxacilline', 'augmentin', 'extencilline', 'benzathine', 'piperacilline'],
  betalactamine: ['amoxicilline', 'ampicilline', 'cef', 'penicilline', 'augmentin'],
  cephalosporine: ['ceftriaxone', 'cefixime', 'cefuroxime', 'cefalexine', 'cefotaxime', 'cefadroxil', 'cefpodoxime'],
  sulfamide: ['cotrimoxazole', 'sulfamethoxazole', 'bactrim', 'sulfadoxine', 'fansidar', 'sulfadiazine'],
  'anti-inflammatoire': ['ibuprofene', 'diclofenac', 'ketoprofene', 'naproxene', 'piroxicam', 'indometacine', 'aspirine', 'meloxicam'],
  aspirine: ['aspirine', 'acide acetylsalicylique', 'kardegic'],
  quinine: ['quinine', 'quinimax'],
  codeine: ['codeine', 'tramadol', 'morphine'],
}

const MOTS_VIDES = ['allergie', 'allergique', 'allergies', 'intolerance', 'aux', 'les', 'des', 'une', 'test']

/** Termes significatifs d'une allergie, au singulier (« penicillines » → penicilline). */
function termes(m) {
  return m.split(/[^a-z0-9]+/).filter(t => t.length >= 3 && !MOTS_VIDES.includes(t)).map(t => (t.length > 4 && t.endsWith('s') ? t.slice(0, -1) : t))
}

/**
 * Allergie connue touchée par ce médicament, ou null : chaque terme de
 * l'allergie se retrouve dans le nom (« artemether » dans « Artéméther-luméfantrine »),
 * ou le médicament est une molécule de la famille citée. Même règle que le serveur.
 */
export function allergieDe(medicament, mots) {
  const nom = sansAccents(medicament)
  if (!nom.trim()) return null
  for (const m of mots) {
    const t = termes(m)
    if (t.length && t.every(x => nom.includes(x))) return m
    const famille = Object.entries(FAMILLES).find(([cle]) => m.includes(cle))
    if (famille && famille[1].some(mol => nom.includes(mol))) return m
  }
  return null
}

/** Allergies citées (« Pénicilline, sulfamides » → [penicilline, sulfamides]) ; les parenthèses sont ignorées. */
export function motsAllergies(texte) {
  return sansAccents(texte).replace(/\([^)]*\)/g, ' ')
    .split(/[,;/\n]| et /).map(m => m.replace(/\s+/g, ' ').trim())
    .filter(m => m.length >= 4 && !['aucune', 'aucun', 'neant', 'rien'].includes(m) && termes(m).length)
}

export default {
  name: 'PrescriptionOrdonnance',
  props: {
    modelValue: { type: Array, default: () => [] },
    frequences: { type: Array, default: () => [] },
    durees: { type: Array, default: () => [] },
    conditionnements: { type: Array, default: () => [] },
    allergies: { type: String, default: '' },
    pharmacie: Boolean,
    lectureSeule: Boolean,
  },
  emits: ['update:modelValue'],
  computed: {
    mots() { return motsAllergies(this.allergies) },
    /** Référentiels pas encore configurés par la clinique (listes déroulantes vides). */
    listesVides() {
      return [
        { liste: this.conditionnements, libelle: 'formes', to: '/conditionnements' },
        { liste: this.frequences, libelle: 'fréquences', to: '/frequences' },
        { liste: this.durees, libelle: 'durées', to: '/duree_medicaments' },
      ].filter(x => !x.liste.length)
    },
  },
  methods: {
    /** L'utilisateur peut ouvrir cette page de configuration (permissions de la route). */
    peutOuvrir(to) {
      const perms = this.$router.resolve(to)?.meta?.permissions || []
      return !perms.length || this.$store.getters.hasAnyPermission(perms)
    },
    modifiable(l) { return !this.lectureSeule && !l.delivre },
    nom(liste, id) { return liste.find(x => x.id === id)?.nom || '' },
    allergie(l) { return allergieDe(l.medicament, this.mots) },
    /** Le médecin garde la décision : il maintient la prescription malgré l'allergie, avec un motif tracé. */
    async maintenir(l) {
      const r = await demanderMotif({
        titre: `Maintenir « ${l.medicament} »`,
        message: `Le patient est allergique à « ${this.allergie(l)} ». Si vous maintenez la prescription, le motif est inscrit au dossier et affiché au pharmacien.`,
        libelle: 'Motif',
        placeholder: 'Ex. : allergie ancienne non confirmée, pas d\'alternative, surveillance prévue',
        libelleValider: 'Maintenir la prescription',
      })
      if (!r?.motif) return
      l.motifAllergie = r.motif
      l.allergieConfirmeePar = ''
    },
    chercher(l) {
      // Nom modifié après le choix d'un produit : ce n'est plus ce produit
      if (l.produitId && l.medicament !== l.nomProduit) {
        l.produitId = null
        l.stock = null
      }
      if (!this.pharmacie || String(l.medicament || '').trim().length < 2) {
        l.propositions = []
        return
      }
      clearTimeout(l.minuteur)
      l.minuteur = setTimeout(async () => {
        try {
          const res = await api.liste('prescription/produits', { q: l.medicament.trim() })
          l.propositions = res.data.data || []
          l.ouvert = true
        } catch {
          l.propositions = []
        }
      }, 250)
    },
    choisir(l, p) {
      l.medicament = p.nom
      l.produitId = p.id
      l.nomProduit = p.nom
      l.stock = p.stock
      l.ouvert = false
    },
    fermerPlusTard(l) { setTimeout(() => { l.ouvert = false }, 150) },
    ajouter() { this.$emit('update:modelValue', [...this.modelValue, ligneOrdonnance()]) },
    retirer(i) { this.$emit('update:modelValue', this.modelValue.filter((_, k) => k !== i)) },
  },
}
</script>

<style scoped>
.ligne { border: 1px solid #e3e8ef; border-radius: 8px; padding: 10px; background: #fff; }
.ligne.alerte { border-color: #f1aeb5; background: #fff8f8; }
.propositions { z-index: 20; max-height: 260px; overflow-y: auto; }
</style>
